<template>
  <div style="width: 100%">
    <div
        :class="cssClasses"
        ref="elementRef"
        :style="{
          height: elementInfo.vertical ? elementInfo.height + 'px' : '100%',
          display: 'flex',
          flexDirection: 'column',
        }"
    >

      <div
          v-for="(row, rowIndex) in chosenLayout.children" :key="row.id" class="layout-row"
          :style="{
            alignItems: 'stretch', flex: getFlex(row), justifyContent: 'center',
            ...(row.subtype === 'footer'
              ? { width: '1366px', position: 'absolute', bottom: '0px', zIndex: -1 }
              : {})
          }">
        <div
            v-for="child in row.children"
            :key="child.id"
            :class="{
                    'section-col': true,
                    'show-section-col': toolbarState === 'section',
                    [child.widgetClass]: child.widgetClass
                  }"

            :style="getParentStyle(child)"
        >

          <NotDragDropElement
              v-if="child.dragDropElement && (!child.gateSection || !gateContent)"
              :sectionId="child.id"
              :sectionRowId="row.id"
              :sectionStyle="child.style"
              :elementInfo="child.dragDropElement"
              :elementIndex="0"
              :isMultiSelect="false"
              :sectionUsePrototypeTile="child.usePrototypeTile"
              :sectionPrototypeTileStyle="child.prototypeTileStyle"
              v-show="true"
          ></NotDragDropElement>

        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, PropType, ref, watch} from 'vue'
import {PPTSlideLayoutElement} from '@/types/slides'
import {useMainStore, useSlidesStore} from '@/store'

import 'bootstrap/dist/css/bootstrap.css';

import {storeToRefs} from 'pinia'
import NotDragDropElement from '@/views/Viewer/Canvas/NotDragDropElement/index.vue'
import { applyRules } from "@/utils/common";
import useMicroservice from "@/hooks/useMicroservice";
const { getCustomerFonts } = useMicroservice()

const props = defineProps({
  elementInfo: {
    type: Object as PropType<PPTSlideLayoutElement>,
    required: true,
  }
})

const cssClasses = ref('element-content dragdrop')
const elementRef = ref<HTMLElement | null>(null)

const { toolbarState, gateContent } = storeToRefs(useMainStore())

const { editor } = storeToRefs(useMainStore())
const slidesStore = useSlidesStore()
const { theme, rules } = storeToRefs(slidesStore)
const chosenLayout = ref([])

const fontFamily = computed(() => {
  if (theme.value.fontFaces) {
    const fontExists = theme.value.fontFaces.some(
        (font) => font.fontFamily === theme.value.fontName
    )

    return fontExists ? `"${theme.value.fontName}"` : theme.value.fontName
  } else {
    return theme.value.fontName
  }
})


const getFlex = (row) => {
  for (const column of row.children) {
    if (column.style.containerHeight) {
      return 0
    }

  }
  return '1'
}

onMounted(async () => {
  updateLayout(props.elementInfo.layout)
  chosenLayout.value = props.elementInfo.layout

  if (!theme.value.fontFaces) {
    const fonts = await getCustomerFonts()

    if (fonts) {
      theme.value.fontFaces = fonts

      injectFontFaces()
      theme.value.fontFaces.forEach(loadFont)
    }
  } else {
    injectFontFaces()
    theme.value.fontFaces.forEach(loadFont)
  }
})

const getAttr = (element, all, which) => {
  if (!element?.style || (!element?.style[all] && !element?.style[which])) {
    return 0
  }
  return element?.style[all] ? px(element?.style[all], 0) : px(element?.style[which], 0)
}

const getAttrDefault = (element, all, which, defaultValue) => {
  if (!element?.style || (!element?.style[all] && !element?.style[which])) {
    return defaultValue
  }
  return element?.style[all] ? px(element?.style[all], 0) : px(element?.style[which], 0)
}


const px = (input, defaultValue) => {
  if (input) {
    return `${input}px`
  }
  else {
    return defaultValue
  }
}

const getParentStyle = (element) => {
  const borderColor = element?.style?.borderColor

  const style = {
    marginTop: getAttr(element, 'margin', 'marginTop'),
    marginRight: getAttrDefault(element, 'margin', 'marginRight', 'auto'),
    marginBottom: getAttr(element, 'margin', 'marginBottom'),
    marginLeft: getAttrDefault(element, 'margin', 'marginLeft', 'auto'),
    paddingTop: getAttr(element, 'padding', 'paddingTop'),
    paddingRight: getAttr(element, 'padding', 'paddingRight'),
    paddingBottom: getAttr(element, 'padding', 'paddingBottom'),
    paddingLeft: getAttr(element, 'padding', 'paddingLeft'),
    justifyContent: element?.style?.justifyContent !== '' ? element?.style?.justifyContent : element?.style?.align,
    alignItems: element?.style?.valign,
    borderTop: `${getAttr(element, 'borderWidth', 'borderTop')} solid ${borderColor}`,
    borderRight: `${getAttr(element, 'borderWidth', 'borderRight')} solid ${borderColor}`,
    borderBottom: `${getAttr(element, 'borderWidth', 'borderBottom')} solid ${borderColor}`,
    borderLeft: `${getAttr(element, 'borderWidth', 'borderLeft')} solid ${borderColor}`,
    borderRadius: px(element?.style?.borderRadius, '0'),
    backgroundColor: element?.style?.backgroundColor,
    height: px(element?.style?.containerHeight,''),
    width: element?.style?.containerWidth || !element['flex-grow'] ? px(element?.style?.containerWidth,'100%') : '',
    maxWidth: px(element?.style?.maxWidth,'100%'),
  }

  if (!element?.style?.containerWidth) {
    style.flexGrow = element['flex-grow'] || '1'
    style.flexShrink = 0
    style.flexBasis = 0
    style.display = element['hidden'] ? 'none' : 'flex'
  }

  if (element.ruleIds && element.type !== 'table') {
    const results = applyRules(element, rules.value);
    return { ...style, ...results };
  } else {
    element.alternateColor = null;
  }

  return style
}

const updateLayout =  (layout) => {
  const dragDrops = slidesStore.getElementsByType('dragdrop')

  for (const row of layout.children) {
    for (const column of row.children) {
      for (const dragDrop of dragDrops) {
        if (dragDrop.id === column.id) {
          column.dragDropElement = dragDrop
        }
      }
    }
  }
}

const loadFont = (font) => {
  const fontFace = new FontFace(font.fontFamily, `url(${font.src})`, {
    weight: font.fontWeight,
    style: font.fontStyle,
  })

  fontFace.load().then((loadedFont) => {
    document.fonts.add(loadedFont)
  }).catch((error) => {
    console.error('Failed to load font:', error)
  })
}

const dynamicFontFace = computed(() => {
  if (!theme.value.fontFaces) return null

  return theme.value?.fontFaces
      .map(
          (font) => `
      @font-face {
        font-family: '${font.fontFamily}';
        src: url('${font.src}');
        font-weight: ${font.fontWeight};
        font-style: ${font.fontStyle};
      }
    `
      )
      .join("\n")
})

watch(dynamicFontFace, () => {
  injectFontFaces()
})

function injectFontFaces() {
  let styleElement = document.getElementById('dynamic-font-faces')
  if (styleElement) {
    styleElement.remove()
  }

  styleElement = document.createElement('style')
  styleElement.id = 'dynamic-font-faces'
  styleElement.innerHTML = dynamicFontFace.value

  document.head.appendChild(styleElement)
}

</script>
<style lang="scss" scoped>
.layout-row {
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
}

.section-col {
  font-family: v-bind(fontFamily);
  user-select: none;
  box-sizing: border-box;
  width: 100%;
  display: block;
  border-radius: 10px;
}
</style>
